import { t } from '@trimble/i18n';
import {
  CapabilitiesCollection,
  CapabilityCoordinatorBase,
  CapabilityRegistryContainer,
} from '@trimble/web3d';
import { PointCloudCapabilityProvider } from '@trimble/web3d-providers';
import {
  CrossPaneFrustumsCapability,
  FrustumCapability,
  ImageCapabilityBase,
  ImagePointcloudCapability,
  MapNavigationCapability,
  MapStationCapability,
  StationsNavigationCapability,
} from '@trimble/web3d-capabilities';
import {
  Accessor,
  createEffect,
  createMemo,
  createRoot,
  createSignal,
  Setter,
  untrack,
} from 'solid-js';
import { ViewModeCapabilityCoordinator } from './view-mode-capability-coordinator';
import { ViewMode } from '../../view-mode';

type FeatureCapability =
  | PointCloudCapabilityProvider
  | ImagePointcloudCapability
  | FrustumCapability
  | CrossPaneFrustumsCapability
  | CapabilitiesCollection<ImageCapabilityBase>
  | StationsNavigationCapability
  | MapStationCapability
  | MapNavigationCapability;

type FeatureToggleMap = Partial<
  Record<ViewMode['type'], () => FeatureCapability | undefined>
>;

export enum FeatureType {
  PointClouds = 'pointClouds',
  Frustum = 'frustum',
  Image = 'image',
  StationMarkers = 'stationMarkers',
  NavigationPath = 'navigationPath',
}

export interface FeatureToggleConfig {
  key: string;
  label: string;
  type: FeatureType;
}

export const toggleFeatures: FeatureToggleConfig[] = [
  {
    key: 'pointClouds',
    label: t('mobileMapping.featureToggle.pointClouds'),
    type: FeatureType.PointClouds,
  },
  {
    key: 'frustum',
    label: t('mobileMapping.featureToggle.frustum'),
    type: FeatureType.Frustum,
  },
  {
    key: 'image',
    label: t('mobileMapping.featureToggle.image'),
    type: FeatureType.Image,
  },
  {
    key: 'stationMarkers',
    label: t('mobileMapping.featureToggle.stationMarkers'),
    type: FeatureType.StationMarkers,
  },
  {
    key: 'navigationPath',
    label: t('mobileMapping.featureToggle.navigationPath'),
    type: FeatureType.NavigationPath,
  },
];

export interface FeatureToggle {
  feature: FeatureToggleConfig;
  capability: FeatureCapability;
  visible: Accessor<boolean>;
  toggle(): void;
}

export class FeatureToggleCapabilityCoordinator extends CapabilityCoordinatorBase<CapabilityRegistryContainer> {
  public readonly features: Accessor<FeatureToggle[]>;
  private readonly setFeatures: Setter<FeatureToggle[]>;

  /**
   * Disposers for in-flight {@link applyFeatureToggleStateReactively} roots,
   * so {@link cleanup} can cancel any pending restoration and stop it from
   * toggling features on a defunct pane.
   */
  private readonly pendingReactiveApplies = new Set<() => void>();

  constructor(
    protected readonly registryContainer: CapabilityRegistryContainer
  ) {
    super(registryContainer);

    const [features, setFeatures] = createSignal<FeatureToggle[]>([]);
    this.features = features;
    this.setFeatures = setFeatures;

    const viewModeCoordinator = createMemo(() =>
      this.registryContainer.get(ViewModeCapabilityCoordinator)()
    );

    createEffect(() => {
      const coord = viewModeCoordinator();
      if (!coord) return;

      const viewMode = coord.viewMode();
      const features = toggleFeatures
        .map((feature) => {
          const featureCapability = this.getFeatureCapability(
            feature.type,
            viewMode.type
          );
          if (!featureCapability) return;

          const capability = featureCapability();
          if (!capability) return;

          return this.createFeatureToggle(feature, capability);
        })
        .filter((feature) => feature !== undefined);

      this.setFeatures(features);
    });
  }

  private getFeatureCapability(
    featureType: FeatureType,
    viewModeType: ViewMode['type']
  ): (() => FeatureCapability | undefined) | undefined {
    const lookup: Record<FeatureType, FeatureToggleMap> = {
      pointClouds: {
        ['3D']: () =>
          this.registryContainer.get(PointCloudCapabilityProvider)(),
        ['Image']: () =>
          this.registryContainer.get(ImagePointcloudCapability)(),
        ['Panorama']: () =>
          this.registryContainer.get(ImagePointcloudCapability)(),
      },
      // TODO: Remove FrustumCapability once CrossPaneFrustumsCapability is fully deployed
      frustum: {
        ['2D']: () =>
          this.registryContainer.get(CrossPaneFrustumsCapability)() ??
          this.registryContainer.get(FrustumCapability)(),
        ['3D']: () => this.registryContainer.get(CrossPaneFrustumsCapability)(),
      },
      image: {
        ['Image']: () =>
          this.registryContainer.get(
            CapabilitiesCollection<ImageCapabilityBase>
          )(),
        ['Panorama']: () =>
          this.registryContainer.get(
            CapabilitiesCollection<ImageCapabilityBase>
          )(),
      },
      stationMarkers: {
        ['3D']: () =>
          this.registryContainer.get(StationsNavigationCapability)(),
        ['2D']: () => this.registryContainer.get(MapStationCapability)(),
        ['Panorama']: () =>
          this.registryContainer.get(
            CapabilitiesCollection<ImageCapabilityBase>
          )(),
      },
      navigationPath: {
        ['3D']: () =>
          this.registryContainer.get(StationsNavigationCapability)(),
        ['2D']: () => this.registryContainer.get(MapStationCapability)(),
        ['Panorama']: () =>
          this.registryContainer.get(
            CapabilitiesCollection<ImageCapabilityBase>
          )(),
      },
    };

    const featureConfig = lookup[featureType];
    return featureConfig[viewModeType];
  }

  private createFeatureToggle(
    feature: FeatureToggleConfig,
    featureCapibility: FeatureCapability
  ): FeatureToggle | undefined {
    const capability =
      featureCapibility instanceof CapabilitiesCollection
        ? featureCapibility.capabilities.find((c) => c.isActive())
        : featureCapibility;

    if (!capability) return;

    const visible = createMemo(() => {
      switch (true) {
        case capability instanceof PointCloudCapabilityProvider:
          return capability.isPointCloudsVisible();

        case capability instanceof StationsNavigationCapability &&
          feature.type === FeatureType.StationMarkers:
          return capability.isStationMarkersVisible();

        case capability instanceof StationsNavigationCapability &&
          feature.type === FeatureType.NavigationPath:
          return capability.isNavigationPathVisible();

        case capability instanceof ImageCapabilityBase:
          return capability.isImageVisible();

        // Show the effective state (user override or coordinator-driven
        // auto-show), so the dropdown checkbox matches what's on screen.
        case capability instanceof FrustumCapability:
          return capability.userIntent() ?? capability.isActive();

        case capability instanceof ImagePointcloudCapability:
        case capability instanceof CrossPaneFrustumsCapability:
          return capability.isActive();

        default:
          return capability.isEnabled();
      }
    });

    // eslint-disable-next-line sonarjs/cognitive-complexity -- SonarJS trial (NUMARK-1243): complexity 18 > 15 allowed; flagged for later refactor, not fixed here
    const toggle = (): void => {
      switch (true) {
        case capability instanceof PointCloudCapabilityProvider:
          capability.setIsPointCloudsVisible(
            !capability.isPointCloudsVisible()
          );
          break;

        case capability instanceof StationsNavigationCapability &&
          feature.type === FeatureType.StationMarkers:
          capability.setIsStationMarkersVisible(
            !capability.isStationMarkersVisible()
          );
          break;

        case capability instanceof StationsNavigationCapability &&
          feature.type === FeatureType.NavigationPath:
          capability.setIsNavigationPathVisible(
            !capability.isNavigationPathVisible()
          );
          break;

        case capability instanceof ImageCapabilityBase:
          capability.setIsImageVisible(!capability.isImageVisible());
          break;

        case capability instanceof ImagePointcloudCapability:
          if (capability.isActive()) capability.deactivate();
          else capability.activate();
          break;

        case capability instanceof FrustumCapability: {
          // Invert the effective state — not raw `userIntent` — so that
          // toggling off while auto-show is driving the frustum on records
          // an explicit `false` that overrides auto-show.
          const nextIntent = !(
            capability.userIntent() ?? capability.isActive()
          );
          capability.setUserIntent(nextIntent);
          const externalFrustums = this.registryContainer.get(
            CrossPaneFrustumsCapability
          )();
          if (externalFrustums) {
            if (nextIntent) externalFrustums.activate();
            else externalFrustums.deactivate();
          }
          break;
        }

        case capability instanceof CrossPaneFrustumsCapability: {
          if (capability.isActive()) capability.deactivate();
          else capability.activate();
          // Mirror onto the per-pane FrustumCapability so the coordinator's
          // tri-state override (`userIntent() ?? autoShow`) sees an explicit
          // user toggle and doesn't fight it via auto-show.
          const frustum = this.registryContainer.get(FrustumCapability)();
          frustum?.setUserIntent(capability.isActive());
          break;
        }

        default:
          if (capability.isEnabled()) capability.disable();
          else capability.enable();
      }
    };

    return { feature, capability, visible, toggle };
  }

  public getFeatureToggleState(): Record<string, boolean> {
    const state: Record<string, boolean> = {};
    for (const ft of this.features()) {
      state[ft.feature.key] = ft.visible();
    }
    return state;
  }

  /**
   * Apply saved feature-toggle state reactively, re-applying whenever the
   * underlying features list changes (e.g. as capabilities register on a
   * freshly created pane). Resolves once every desired key has been applied,
   * or after `timeoutMs` as a safety net for keys that never appear (e.g. a
   * feature that isn't applicable in the current view mode).
   */
  public applyFeatureToggleStateReactively(
    state: Record<string, boolean>,
    timeoutMs: number
  ): Promise<void> {
    const desiredKeys = Object.keys(state);
    if (desiredKeys.length === 0) return Promise.resolve();

    return new Promise<void>((resolve) => {
      this.startReactiveApply(state, desiredKeys, timeoutMs, resolve);
    });
  }

  private startReactiveApply(
    state: Record<string, boolean>,
    desiredKeys: readonly string[],
    timeoutMs: number,
    resolve: () => void
  ): void {
    createRoot((disposeRoot) => {
      const applied = new Set<string>();
      const finish = (): void => {
        clearTimeout(timer);
        this.pendingReactiveApplies.delete(finish);
        disposeRoot();
        resolve();
      };
      this.pendingReactiveApplies.add(finish);
      const timer = setTimeout(finish, timeoutMs);

      createEffect(() => {
        const features = this.features();
        untrack(() => {
          this.applyDesiredState(features, state, applied);
          if (desiredKeys.every((k) => applied.has(k))) {
            finish();
          }
        });
      });
    });
  }

  /** Toggle features whose visibility differs from `state`, recording the keys handled. */
  private applyDesiredState(
    features: readonly FeatureToggle[],
    state: Record<string, boolean>,
    applied: Set<string>
  ): void {
    for (const ft of features) {
      const key = ft.feature.key;
      const desired = state[key];
      // eslint-disable-next-line sonarjs/different-types-comparison -- index access can be undefined at runtime (noUncheckedIndexedAccess is off)
      if (desired === undefined) continue;
      if (ft.visible() !== desired) {
        ft.toggle();
      }
      applied.add(key);
    }
  }

  public cleanup(): Promise<void> {
    // Cancel any in-flight reactive restoration so its root/timer can't keep
    // toggling features after the coordinator is gone. `finish` removes itself
    // from the set, so snapshot first to avoid mutating during iteration.
    for (const finish of [...this.pendingReactiveApplies]) {
      finish();
    }
    return Promise.resolve();
  }
}
